import { Folder, FolderContent } from '@/api/generatedApi';
import { Ellipsis } from '@/components/Ellipsis';
import { FolderActionRow } from '@/components/FolderActionRow/FolderActionRow';
import { FolderIcon } from '@/components/FolderIcon';
import { pluralize } from '@/utils/format';
import { Box, Tooltip, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { DND_NO_SELECT_STYLES, DND_OPACITY } from '@/components/dnd';
import { useBulkSelectionContext } from '@/components/ContentsTable/BulkSelectionContext';
import { memo, useMemo } from 'react';
import { InlineLink, MediaArea, StyledCard, StyledCardContent, ThumbnailLink } from './cardStyles';

const FolderCardVisual = memo(function FolderCardVisual({
  folder,
  linkTo,
  onContentRemoved,
}: {
  folder: Folder;
  linkTo: { pathname: string; search: string };
  onContentRemoved?: (id: string) => void;
}) {
  const countsText = [
    folder.assetsCount ? pluralize(folder.assetsCount, 'asset', 'assets') : '',
    folder.foldersCount ? pluralize(folder.foldersCount, 'folder', 'folders') : '',
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <>
      <MediaArea>
        <Tooltip title={countsText && `Contains ${countsText}`} arrow placement='top'>
          <ThumbnailLink to={linkTo} draggable={false} sx={{ width: 105, height: 105 }}>
            <Box sx={{ transform: 'scale(2.5)' }}>
              <FolderIcon />
            </Box>
          </ThumbnailLink>
        </Tooltip>
      </MediaArea>
      <StyledCardContent>
        <InlineLink to={linkTo} draggable={false} sx={{ display: 'block', overflow: 'hidden', minWidth: 0 }}>
          <Typography variant='body2' component='span' sx={{ fontWeight: 600, display: 'block', overflow: 'hidden' }}>
            <Ellipsis text={folder.name} />
          </Typography>
        </InlineLink>
        {folder.description && (
          <Ellipsis
            text={folder.description}
            sx={{ fontSize: '0.75rem', opacity: 0.6 }}
            tooltipProps={{ placement: 'bottom-start' }}
          />
        )}
      </StyledCardContent>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', px: 1.5, py: 0.5 }}>
        <FolderActionRow
          folder={folder}
          onDelete={() => onContentRemoved?.(folder.id)}
          sx={{
            justifyContent: 'center',
            '& > *': { display: 'inline-flex', alignItems: 'center' },
            '& .MuiIconButton-root': { padding: '6px' },
            '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
          }}
        />
      </Box>
    </>
  );
});

export const FolderContentCard = ({
  folder,
  onContextMenu,
  onContentRemoved,
}: {
  folder: Folder;
  onContextMenu?: (event: React.MouseEvent) => void;
  onContentRemoved?: (id: string) => void;
}) => {
  const location = useLocation();
  const bulkSelection = useBulkSelectionContext<FolderContent>();

  const selectedRows = bulkSelection?.selectedRows ?? [];
  const isSelected = selectedRows.some(r => r.id === folder.id);
  const singleItemToDrag = useMemo(() => [{ ...folder, contentType: 'folder' } as FolderContent], [folder]);
  const contentsToDrag = isSelected && selectedRows.length > 0 ? selectedRows : singleItemToDrag;

  const {
    attributes,
    listeners,
    setNodeRef: setDragReference,
    isDragging,
  } = useDraggable({
    id: `drag-card-${folder.id}`,
    data: { contents: contentsToDrag },
  });

  const { setNodeRef: setDropReference, isOver } = useDroppable({
    id: `drop-card-${folder.id}`,
    data: { type: 'folder-drop-target', folderId: folder.id, folderName: folder.name },
  });

  const setNodeReference = (node: HTMLElement | null) => {
    setDragReference(node);
    setDropReference(node);
  };

  const folderLinkTo = useMemo(
    () => ({ pathname: location.pathname, search: `?folderId=${folder.id}` }),
    [location.pathname, folder.id]
  );

  const cardSx = useMemo(
    () => ({
      ...DND_NO_SELECT_STYLES,
      ...(isDragging && { opacity: DND_OPACITY.dragging }),
    }),
    [isDragging]
  );

  return (
    <StyledCard
      data-id={folder.id}
      data-field='name'
      data-dnd-drop-target={String(isOver)}
      onContextMenu={onContextMenu}
      ref={setNodeReference}
      {...attributes}
      {...listeners}
      sx={cardSx}
    >
      <FolderCardVisual folder={folder} linkTo={folderLinkTo} onContentRemoved={onContentRemoved} />
    </StyledCard>
  );
};
